import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import DrillDownDrawer, { cellDate, cellUsd, cellMono, cellBadge } from "../components/DrillDownDrawer";
import QuoteDetailDialog from "../components/QuoteDetailDialog";

const RANGE_IDS = ["7d", "30d", "90d", "12m", "all"];
const VIEW_IDS = ["total", "mitigated", "active"];

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtTons = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })} t`;

export default function AdminDashboard() {
  const { t: tr } = useTranslation();
  const RANGES = useMemo(() => RANGE_IDS.map((id) => ({ id, label: tr(`admin.r_${id}`) })), [tr]);
  const VIEWS  = useMemo(() => VIEW_IDS.map((id)  => ({ id, label: tr(`admin.v_${id}`) })),  [tr]);
  const [range, setRange] = useState("30d");
  const [view, setView] = useState("total");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { title, subtitle, endpoint, params, columns, csvFilename }
  const [quoteDetail, setQuoteDetail] = useState(null); // currently-open quote (row click)

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/dashboard?range=${range}&view=${view}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range, view]);

  const t = data?.totals;
  const series = data?.series;

  const usersSeries = useMemo(() => {
    if (!series) return [];
    // Merge signups + kyb_approved into one frame keyed by bucket.
    const byBucket = new Map();
    series.signups.forEach((s) => byBucket.set(s.bucket, { bucket: s.bucket, signups: s.count, kyb: 0 }));
    series.kyb_approved.forEach((k) => {
      const row = byBucket.get(k.bucket) || { bucket: k.bucket, signups: 0, kyb: 0 };
      row.kyb = k.count;
      byBucket.set(k.bucket, row);
    });
    return Array.from(byBucket.values()).sort((a, b) => a.bucket.localeCompare(b.bucket));
  }, [series]);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tag mb-4">Admin · Dashboard</div>
          <h1 className="font-serif text-5xl tracking-tight">{tr("admin.operations_overview")}</h1>
          <p className="text-ink2 mt-3 max-w-2xl text-sm">
            {tr("admin.overview_sub")}
          </p>
        </div>
        <SendDigestButton />
      </section>

      {/* Range + view selectors */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap gap-4 border border-line bg-surface p-4">
          <div>
            <div className="label-tag mb-2">{tr("admin.time_range")}</div>
            <div className="flex flex-wrap gap-1">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  data-testid={`range-${r.id}`}
                  onClick={() => setRange(r.id)}
                  className={`px-4 py-2 text-[11px] font-mono uppercase tracking-wider border ${
                    range === r.id ? "bg-ink text-white border-ink" : "border-line text-ink2 hover:border-ink hover:text-ink"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-tag mb-2">{tr("admin.sales_view")}</div>
            <div className="flex flex-wrap gap-1">
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  data-testid={`view-${v.id}`}
                  onClick={() => setView(v.id)}
                  className={`px-4 py-2 text-[11px] font-mono uppercase tracking-wider border ${
                    view === v.id ? "bg-ink text-white border-ink" : "border-line text-ink2 hover:border-ink hover:text-ink"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {loading && <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 text-center text-ink2 font-mono text-sm">Loading…</div>}
      {!loading && data && (
        <>
          {/* KPI cards */}
          <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpis">
            <Kpi
              label={tr("admin.kpi_sales")}
              value={fmtUsd(t.sales_usd[view])}
              hint={`${fmtTons(t.sales_tons[view])} · ${view.replace("_", " ")}`}
              testid="kpi-sales"
              onClick={() => setDrill({
                title: view === "mitigated" ? tr("admin.title_mitigation_tx") : tr("admin.title_sales_tx"),
                subtitle: tr("admin.sub_tx_view", { range: RANGES.find((r) => r.id === range)?.label || range, view: VIEWS.find((v) => v.id === view)?.label || view }),
                endpoint: "/admin/dashboard/transactions",
                params: { range, view },
                csvFilename: `transactions_${range}_${view}.csv`,
                columns: [
                  { key: "created_at", label: tr("admin.col_date"), render: (r) => cellDate(r.created_at) },
                  { key: "user_email", label: tr("admin.col_buyer") },
                  { key: "project_name", label: tr("admin.col_project") },
                  { key: "tons", label: tr("admin.col_tons"), render: (r) => cellMono(Number(r.tons || 0).toFixed(2)) },
                  { key: "amount_usd", label: tr("admin.col_usd"), render: (r) => cellUsd(r.amount_usd) },
                  { key: "currency", label: tr("admin.col_currency"), render: (r) => cellMono(r.currency) },
                ],
              })}
            />
            <Kpi
              label={tr("admin.kpi_users")}
              value={t.signups.toLocaleString()}
              hint={tr("admin.kpi_users_hint", { count: t.kyb_approved.toLocaleString() })}
              testid="kpi-signups"
              onClick={() => setDrill({
                title: tr("admin.title_users"),
                subtitle: tr("admin.sub_users", { range: RANGES.find((r) => r.id === range)?.label || range }),
                endpoint: "/admin/dashboard/users",
                params: { range },
                csvFilename: `users_${range}.csv`,
                columns: [
                  { key: "created_at", label: "Signed up", render: (r) => cellDate(r.created_at) },
                  { key: "email", label: "Email" },
                  { key: "name", label: "Name" },
                  { key: "company_name", label: "Company" },
                  { key: "country", label: "Country", render: (r) => cellMono(r.country) },
                  { key: "signatory_name", label: "Signatory" },
                  { key: "role", label: "Role", render: (r) => cellBadge(r.role, r.role === "admin" ? "ink" : r.role === "viewer" ? "muted" : "leaf") },
                  { key: "kyc_status", label: "KYC", render: (r) => cellBadge(r.kyc_status,
                      r.kyc_status === "verified" ? "leaf" :
                      r.kyc_status === "review" ? "danger" : "muted") },
                ],
              })}
              secondaryAction={{
                label: "View KYB approved →",
                onClick: () => setDrill({
                  title: tr("admin.title_users_kyb"),
                  subtitle: tr("admin.sub_kyb", { range: RANGES.find((r) => r.id === range)?.label || range }),
                  endpoint: "/admin/dashboard/users",
                  params: { range, kyc: "verified" },
                  csvFilename: `users_kyb_approved_${range}.csv`,
                  columns: [
                    { key: "created_at", label: tr("admin.col_signed_up"), render: (r) => cellDate(r.created_at) },
                    { key: "email", label: tr("admin.col_email") },
                    { key: "name", label: tr("admin.col_name") },
                    { key: "company_name", label: tr("admin.col_company") },
                    { key: "country", label: tr("admin.col_country"), render: (r) => cellMono(r.country) },
                    { key: "signatory_name", label: tr("admin.col_signatory") },
                    { key: "signatory_position", label: tr("admin.col_position") },
                    { key: "kyc_status", label: tr("admin.col_kyc"), render: (r) => cellBadge(r.kyc_status, "leaf") },
                  ],
                }),
              }}
            />
            <Kpi
              label={tr("admin.kpi_quotes")}
              value={t.quotes.toLocaleString()}
              hint={tr("admin.kpi_quotes_split", {
                single: (t.quotes_single || 0).toLocaleString(),
                bulk: (t.quotes_bulk || 0).toLocaleString(),
              })}
              testid="kpi-quotes"
              onClick={() => setDrill({
                title: tr("admin.title_quotes"),
                subtitle: tr("admin.sub_quotes", { range: RANGES.find((r) => r.id === range)?.label || range }),
                endpoint: "/admin/dashboard/quotes",
                params: { range },
                csvFilename: `quotes_${range}.csv`,
                onRowClick: (row) => setQuoteDetail(row),
                columns: [
                  { key: "created_at", label: tr("admin.col_date"), render: (r) => cellDate(r.created_at) },
                  { key: "ref", label: tr("admin.col_ref"), render: (r) => cellMono(r.ref) },
                  { key: "company_name", label: tr("admin.col_company") },
                  { key: "contact_name", label: tr("admin.col_contact") },
                  { key: "contact_email", label: tr("admin.col_email") },
                  { key: "contact_phone", label: tr("admin.col_phone"), render: (r) => cellMono(r.contact_phone) },
                  { key: "total_usd", label: tr("admin.col_usd"), render: (r) => cellUsd(r.total_usd) },
                  { key: "status", label: tr("admin.col_status"), render: (r) => cellBadge(r.status,
                      r.status === "accepted" ? "leaf" :
                      r.status === "declined" || r.status === "expired" ? "danger" :
                      r.status === "contacted" ? "ink" : "muted") },
                ],
              })}
            />
            <Kpi
              label={tr("admin.kpi_split")}
              value={`${fmtTons(t.sales_tons.active)} / ${fmtTons(t.sales_tons.mitigated)}`}
              hint={tr("admin.kpi_split_hint")}
              testid="kpi-split"
              onClick={() => setDrill({
                title: tr("admin.title_mitigation_retire_tx"),
                subtitle: tr("admin.sub_tx_retire", { range: RANGES.find((r) => r.id === range)?.label || range }),
                endpoint: "/admin/dashboard/transactions",
                params: { range, view: "mitigated" },
                csvFilename: `mitigation_${range}.csv`,
                columns: [
                  { key: "created_at", label: tr("admin.col_date"), render: (r) => cellDate(r.created_at) },
                  { key: "user_email", label: tr("admin.col_mitigated_by") },
                  { key: "project_name", label: tr("admin.col_project") },
                  { key: "tons", label: tr("admin.col_tons"), render: (r) => cellMono(Number(r.tons || 0).toFixed(2)) },
                ],
              })}
            />
          </section>

          {/* Sales over time */}
          <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-line bg-surface p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tag">{tr("admin.sales_over_time", { view: VIEWS.find((v) => v.id === view)?.label || view })}</div>
                <div className="text-[11px] font-mono text-ink2">{tr("admin.usd_day", { unit: data.granularity })}</div>
              </div>
              <div className="h-72" data-testid="chart-sales">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series.sales} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="usdFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0b3d2e" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#0b3d2e" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 3" stroke="#e6e8e6" />
                    <XAxis dataKey="bucket" stroke="#8a8f8b" fontSize={11} tickMargin={6} />
                    <YAxis stroke="#8a8f8b" fontSize={11} tickFormatter={fmtUsd} />
                    <Tooltip formatter={(v, k) => k === "usd" ? fmtUsd(v) : `${v} t`} />
                    <Area type="monotone" dataKey="usd" stroke="#0b3d2e" fill="url(#usdFill)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-line bg-surface p-5">
              <div className="label-tag mb-2">{tr("admin.tonnes_over_time")}</div>
              <div className="h-72" data-testid="chart-tons">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series.sales} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 3" stroke="#e6e8e6" />
                    <XAxis dataKey="bucket" stroke="#8a8f8b" fontSize={11} tickMargin={6} />
                    <YAxis stroke="#8a8f8b" fontSize={11} />
                    <Tooltip formatter={(v) => `${v} t`} />
                    <Bar dataKey="tons" fill="#0b3d2e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Platform growth */}
          <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-line bg-surface p-5">
              <div className="label-tag mb-2">Platform growth · sign-ups vs KYB-approved</div>
              <div className="h-72" data-testid="chart-users">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usersSeries} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 3" stroke="#e6e8e6" />
                    <XAxis dataKey="bucket" stroke="#8a8f8b" fontSize={11} tickMargin={6} />
                    <YAxis stroke="#8a8f8b" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                    <Line type="monotone" dataKey="signups" name="Sign-ups" stroke="#0b3d2e" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="kyb" name="KYB approved" stroke="#c79b3c" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-line bg-surface p-5">
              <div className="label-tag mb-2">{tr("admin.quote_pipeline")}</div>
              <div className="h-72" data-testid="chart-quotes">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series.quotes} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 3" stroke="#e6e8e6" />
                    <XAxis dataKey="bucket" stroke="#8a8f8b" fontSize={11} tickMargin={6} />
                    <YAxis stroke="#8a8f8b" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Quotes" fill="#0b3d2e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {(Object.keys(data.quotes_single_by_status || {}).length > 0 ||
                Object.keys(data.quotes_bulk_by_status || {}).length > 0) && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <QuoteStatusColumn
                    title={tr("admin.pipeline_single")}
                    accent="ink"
                    byStatus={data.quotes_single_by_status || {}}
                    qtype="single"
                    range={range}
                    rangeLabel={RANGES.find((r) => r.id === range)?.label || range}
                    setDrill={setDrill}
                    setQuoteDetail={setQuoteDetail}
                    tr={tr}
                  />
                  <QuoteStatusColumn
                    title={tr("admin.pipeline_bulk")}
                    accent="amber"
                    byStatus={data.quotes_bulk_by_status || {}}
                    qtype="bulk"
                    range={range}
                    rangeLabel={RANGES.find((r) => r.id === range)?.label || range}
                    setDrill={setDrill}
                    setQuoteDetail={setQuoteDetail}
                    tr={tr}
                  />
                </div>
              )}
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 mb-16">
            <p className="text-[11px] font-mono text-ink2 leading-relaxed">
              {tr("admin.footer_note")}
            </p>
            <p className="text-[11px] font-mono text-ink2 mt-2 italic">
              {tr("admin.tip_click")}
            </p>
          </section>
        </>
      )}

      {drill && (
        <DrillDownDrawer
          open={!!drill}
          onOpenChange={(o) => { if (!o) setDrill(null); }}
          {...drill}
        />
      )}

      {quoteDetail && (
        <QuoteDetailDialog
          quote={quoteDetail}
          onClose={() => setQuoteDetail(null)}
          onChanged={() => {
            // Status / reply update — re-fetch dashboard counts so the chips refresh.
            api.get(`/admin/dashboard?range=${range}&view=${view}`).then((r) => setData(r.data)).catch(() => {});
          }}
        />
      )}
    </Layout>
  );
}

function Kpi({ label, value, hint, testid, onClick, secondaryAction }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`border border-line bg-surface p-5 transition-colors ${clickable ? "cursor-pointer hover:border-ink hover:bg-bg/40" : ""}`}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="label-tag">{label}</div>
        {clickable && <span className="text-[10px] font-mono text-ink2">↗</span>}
      </div>
      <div className="mono-num text-2xl text-ink mt-2">{value}</div>
      <div className="text-[11px] font-mono text-ink2 mt-1">{hint}</div>
      {secondaryAction && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); secondaryAction.onClick(); }}
          className="mt-3 text-[10px] font-mono uppercase tracking-wider text-leaf hover:text-ink underline underline-offset-2"
          data-testid={`${testid}-secondary`}
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  );
}

function QuoteStatusColumn({ title, accent, byStatus, qtype, range, rangeLabel, setDrill, setQuoteDetail, tr }) {
  const entries = Object.entries(byStatus || {});
  const totalCount = entries.reduce((sum, [, v]) => sum + (v.count || 0), 0);
  const totalUsd = entries.reduce((sum, [, v]) => sum + (v.usd || 0), 0);
  const headerColor = accent === "amber" ? "text-leaf" : "text-ink";
  const borderColor = accent === "amber" ? "border-leaf/40" : "border-line";
  return (
    <div className={`border ${borderColor} bg-bg/30 p-3`} data-testid={`pipeline-col-${qtype}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className={`label-tag ${headerColor}`}>{title}</div>
        <div className="text-[11px] font-mono text-ink2">
          {totalCount} · {fmtUsd(totalUsd)}
        </div>
      </div>
      {entries.length === 0 ? (
        <div className="text-[11px] font-mono text-ink2 italic py-3">{tr("admin.pipeline_empty")}</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
          {entries.map(([k, v]) => (
            <button
              key={k}
              type="button"
              className="border border-line px-2 py-1 text-left hover:border-ink hover:bg-bg/40 transition-colors"
              data-testid={`quote-status-${qtype}-${k}`}
              onClick={() => setDrill({
                title: `${tr("admin.title_quotes")} · ${qtype.toUpperCase()} · ${k.toUpperCase()}`,
                subtitle: tr("admin.sub_quotes_status", { range: rangeLabel, status: k }),
                endpoint: "/admin/dashboard/quotes",
                params: { range, status: k, type: qtype },
                csvFilename: `quotes_${range}_${qtype}_${k}.csv`,
                onRowClick: (row) => setQuoteDetail(row),
                columns: [
                  { key: "created_at", label: tr("admin.col_date"), render: (r) => cellDate(r.created_at) },
                  { key: "ref", label: tr("admin.col_ref"), render: (r) => cellMono(r.ref) },
                  { key: "company_name", label: tr("admin.col_company") },
                  { key: "contact_name", label: tr("admin.col_contact") },
                  { key: "contact_email", label: tr("admin.col_email") },
                  { key: "contact_phone", label: tr("admin.col_phone"), render: (r) => cellMono(r.contact_phone) },
                  { key: "total_usd", label: tr("admin.col_usd"), render: (r) => cellUsd(r.total_usd) },
                  { key: "settlement_method", label: tr("admin.col_settlement"), render: (r) => cellMono(r.settlement_method) },
                ],
              })}
            >
              <div className="text-ink2 uppercase">{k}</div>
              <div className="text-ink">{v.count} · {fmtUsd(v.usd)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SendDigestButton() {
  const { t: tr } = useTranslation();
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/dashboard/send-digest", {});
      if (r.data?.sent) {
        toast.success(tr("admin.send_digest_ok", { to: r.data.to }));
      } else if (r.data?.reason === "no_recipient") {
        toast.error(tr("admin.send_digest_no_email"));
      } else {
        toast.error(tr("admin.send_digest_fail"));
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || tr("admin.send_digest_fail"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={send}
      disabled={busy}
      data-testid="send-digest-btn"
      className="px-4 py-2 border border-ink text-[11px] font-mono uppercase tracking-wider text-ink hover:bg-ink hover:text-white disabled:opacity-50"
      title={tr("admin.send_digest_tooltip")}
    >
      {busy ? tr("admin.sending") : `${tr("admin.send_digest")} →`}
    </button>
  );
}

